import React, { useState, useRef, useEffect } from "react";

function CategorySelectDropdown({
  value,
  onChange,
  options = [],
  placeholder = "Select Category",
  disabled = false,
  fallbackIcon = "/category.png",
  className = "",
  buttonClassName = "",
  showAllOption = true,
  capitalize = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchFilter, setSearchFilter] = useState("");
  const dropdownRef = useRef(null);

  // Close when clicked outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchFilter("");
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  const selectedItem = (options || []).find(
    (item) => String(item._id) === String(value)
  );

  const formatTitle = (title) => {
    if (!title) return "";
    if (capitalize) {
      return title.charAt(0).toUpperCase() + title.slice(1);
    }
    return title;
  };

  const filteredOptions = (options || []).filter((item) => {
    if (!searchFilter.trim()) return true;
    return (item.title || "")
      .toLowerCase()
      .includes(searchFilter.trim().toLowerCase());
  });

  const selectedIcon = selectedItem
    ? selectedItem.image || selectedItem.icon || fallbackIcon
    : fallbackIcon;

  return (
    <div
      ref={dropdownRef}
      className={`relative w-full ${disabled ? "opacity-50 cursor-not-allowed pointer-events-none" : ""} ${className}`}
    >
      <button
        type="button"
        disabled={disabled}
        onClick={() => setIsOpen((prev) => !prev)}
        className={`appearance-none w-full pl-3 pr-10 py-2.5 rounded-xl border border-gray-200 bg-white shadow-sm text-sm font-semibold text-gray-700 cursor-pointer focus:outline-none focus:ring-2 focus:ring-[var(--secondary-500)] text-left flex items-center gap-2.5 hover:border-[var(--secondary-500)] hover:shadow-md transition-all ${buttonClassName}`}
      >
        <img
          src={selectedIcon}
          alt=""
          className="w-6 h-6 rounded-md object-cover flex-shrink-0 border border-gray-100"
          onError={(e) => {
            e.currentTarget.onerror = null;
            e.currentTarget.src = fallbackIcon;
          }}
        />
        <span className="truncate flex-1">
          {selectedItem ? formatTitle(selectedItem.title) : placeholder}
        </span>
        <div className="absolute right-3.5 inset-y-0 flex items-center pointer-events-none text-gray-400">
          <svg
            className={`w-4 h-4 transition-transform duration-200 ${
              isOpen ? "rotate-180 text-[var(--secondary-500)]" : ""
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth="2"
              d="M19 9l-7 7-7-7"
            />
          </svg>
        </div>
      </button>

      {isOpen && !disabled && (
        <div className="absolute left-0 right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-[0_10px_35px_rgba(0,0,0,0.15)] border border-gray-100 p-2 max-h-72 flex flex-col animate-fadeIn">
          {options.length > 7 && (
            <div className="p-1 mb-1 border-b border-gray-100">
              <input
                type="text"
                value={searchFilter}
                onChange={(e) => setSearchFilter(e.target.value)}
                placeholder="Search..."
                className="w-full px-3 py-1.5 text-xs rounded-lg border border-gray-200 focus:outline-none focus:border-[var(--secondary-500)]"
                onClick={(e) => e.stopPropagation()}
                autoFocus
              />
            </div>
          )}

          <div className="overflow-y-auto max-h-56 space-y-1">
            {showAllOption && !searchFilter && (
              <div
                className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-colors text-sm ${
                  !value
                    ? "bg-pink-50 font-bold text-[var(--secondary-500)]"
                    : "hover:bg-gray-50 text-gray-700"
                }`}
                onClick={() => {
                  onChange("");
                  setIsOpen(false);
                  setSearchFilter("");
                }}
              >
                <img
                  src={fallbackIcon}
                  alt=""
                  className="w-6 h-6 rounded-md object-cover flex-shrink-0 border border-gray-100"
                  onError={(e) => {
                    e.currentTarget.onerror = null;
                    e.currentTarget.src = fallbackIcon;
                  }}
                />
                <span className="truncate">{placeholder}</span>
              </div>
            )}

            {filteredOptions.length > 0 ? (
              filteredOptions.map((item) => {
                const isSelected = String(value) === String(item._id);
                const iconSrc = item.image || item.icon || fallbackIcon;

                return (
                  <div
                    key={item._id}
                    className={`flex items-center gap-2.5 px-3 py-2 rounded-lg cursor-pointer transition-colors text-sm ${
                      isSelected
                        ? "bg-pink-50 font-bold text-[var(--secondary-500)]"
                        : "hover:bg-gray-50 text-gray-700"
                    }`}
                    onClick={() => {
                      onChange(item._id, item);
                      setIsOpen(false);
                      setSearchFilter("");
                    }}
                  >
                    <img
                      src={iconSrc}
                      alt={item.title || ""}
                      className="w-6 h-6 rounded-md object-cover flex-shrink-0 border border-gray-100"
                      onError={(e) => {
                        e.currentTarget.onerror = null;
                        e.currentTarget.src = fallbackIcon;
                      }}
                    />
                    <span className="truncate flex-1">
                      {formatTitle(item.title)}
                    </span>
                    {isSelected && (
                      <span className="text-xs font-bold text-[var(--secondary-500)]">
                        ✓
                      </span>
                    )}
                  </div>
                );
              })
            ) : (
              <div className="px-3 py-3 text-xs text-gray-400 text-center">
                No matching options
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CategorySelectDropdown;
